import { Button, CountrySelect, Header, Icon, LoadingSpinner } from '../components/Generic'
import { observer } from 'mobx-react'
import { useContext, useEffect } from 'react'
import { StoreContext } from '@store/Root.store'
import { onKeyDownHandler } from '@/methods/keyboardAccessibility'
import { useDocumentType } from '@/hooks/useDocumentType'
import { actionReport } from '@/methods/actionReport'
import { cx } from '@/utils/cx'

export const DocumentTypePage = observer(() => {
  const store = useContext(StoreContext)
  const { isMobile } = store.AppState
  const { currentScenarioId, context } = store.ScenarioState
  const { documentTypePage: trans } = store.TranslationsState.translations

  const {
    docType,
    changeSelectedDocumentType,
    country,
    apiCountries,
    countryDocTypes,
    btnLoading,
    getDocLabel,
    getDocIcon,
    setSelectedCountry,
    handleProceed,
  } = useDocumentType(currentScenarioId, context && context.shouldSelectDocument, '', trans)

  useEffect(() => {
    if (context && context.shouldSelectDocument) {
      actionReport({
        type: 'event.onboarding-web.document-selection.PAGE_OPENED',
        payload: {},
      })
    }
  }, [context])

  if (!context || (context && !context.shouldSelectDocument)) {
    return (
      <div className="page-container">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="page-container">
      <Header>{trans.header}</Header>
      <div className="generic__country-wrap">
        <span>{trans.selectCountry}</span>
        {isMobile ? (
          <CountrySelect
            externalList={apiCountries}
            value={country}
            onChange={(value) => setSelectedCountry(value)}
            autoFocus={true}
            defaultMenuIsOpen={false}
            placeholder={trans.inputPlaceholder}
            showCheckmark
          />
        ) : (
          <CountrySelect
            externalList={apiCountries}
            value={country}
            onChange={(value) => setSelectedCountry(value)}
            autoFocus={false}
            defaultMenuIsOpen={false}
            placeholder={trans.inputPlaceholder}
            showCheckmark
          />
        )}
      </div>

      {!!country && !!countryDocTypes && (
        <div className="generic__document-type-wrap">
          <h2 className="generic__document-type-label" data-testid="chooseDocument">
            {trans.chooseDocument}
          </h2>
          {countryDocTypes.map((type) => {
            return isMobile ? (
              <div
                className={cx(
                  'generic__document-picker',
                  '' + type.id === docType ? 'generic__current' : ''
                )}
                onClick={() => changeSelectedDocumentType(type.id)}
                id={type.name}
                tabIndex={0}
                role="button"
                onKeyDown={(event) =>
                  onKeyDownHandler(event, () => changeSelectedDocumentType(type.id))
                }
              >
                <Icon key={type.id} type={getDocIcon(type.name)} size="32px" />
                <div className="generic__document-type-name">
                  <span>{getDocLabel(type.name)}</span>
                  {/* #TODO: Remove when not longer needed, as this condition is temporary */}
                  {context?.configurationId === 'bmw.gr' &&
                    type.name === 'IdentityCard' &&
                    country.value === 'GR' && (
                      <span className="generic__document-type-info">{trans.idCardInfo}</span>
                    )}
                </div>
                <div className="generic__document-checked">
                  <Icon
                    type="selected-document"
                    size="32px"
                    color="var(--symbol-current, black)"
                    visible={`${type.id}` === docType}
                  />
                </div>
              </div>
            ) : (
              <div
                className={cx(
                  'generic__document-picker',
                  '' + type.id === docType ? 'generic__current' : ''
                )}
                onClick={() => changeSelectedDocumentType(type.id)}
                id={type.name}
                tabIndex={0}
                role="button"
                onKeyDown={(event) =>
                  onKeyDownHandler(event, () => changeSelectedDocumentType(type.id))
                }
              >
                <Icon key={type.id} type={getDocIcon(type.name)} size="32px" />
                <div className="generic__document-type-name">
                  <span>{getDocLabel(type.name)}</span>
                  {/* #TODO: Remove when not longer needed, as this condition is temporary */}
                  {context?.configurationId === 'bmw.gr' &&
                    type.name === 'IdentityCard' &&
                    country.value === 'GR' && (
                      <span className="generic__document-type-info">{trans.idCardInfo}</span>
                    )}
                </div>
                <div className="generic__document-checked">
                  <Icon
                    type="selected-document"
                    size="32px"
                    color="var(--symbol-current, black)"
                    visible={`${type.id}` === docType}
                  />
                </div>
              </div>
            )
          })}
          {/* #TODO: Remove when not longer needed, as this condition is temporary */}
          {context?.configurationId === 'bmw.gr' && docType === '340' && country.value === 'GR' && (
            <div className="generic__document-type-warning" data-testid="id-card-warning">
              <div>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7.72676 3.6875C8.73712 1.9375 11.263 1.9375 12.2734 3.6875L18.0469 13.6875C19.0573 15.4375 17.7943 17.625 15.7736 17.625H4.22657C2.20585 17.625 0.942893 15.4375 1.95326 13.6875L7.72676 3.6875ZM11.3352 4.22917C10.7418 3.20139 9.25834 3.20139 8.66495 4.22917L2.89145 14.2292C2.29806 15.2569 3.0398 16.5417 4.22657 16.5417H15.7736C16.9604 16.5417 17.7021 15.2569 17.1087 14.2292L11.3352 4.22917Z"
                    fill="currentColor"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10.0001 6.54167C10.2992 6.54167 10.5417 6.78418 10.5417 7.08333V11.6667C10.5417 11.9658 10.2992 12.2083 10.0001 12.2083C9.70092 12.2083 9.45841 11.9658 9.45841 11.6667V7.08333C9.45841 6.78418 9.70092 6.54167 10.0001 6.54167Z"
                    fill="currentColor"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10.6251 14.375C10.6251 14.7202 10.3453 15 10.0001 15C9.6549 15 9.37508 14.7202 9.37508 14.375C9.37508 14.0298 9.6549 13.75 10.0001 13.75C10.3453 13.75 10.6251 14.0298 10.6251 14.375Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <div>{trans.idCardWarning}</div>
            </div>
          )}
        </div>
      )}

      <div className="layout__content-container-bottom-fixed">
        {!isMobile && <div className="layout__content-container-bottom-left" />}
        <Button onClick={handleProceed} disabled={!(docType && docType.length)} id="page-submit">
          {btnLoading && <LoadingSpinner inButton />}
          <span
            className={cx('generic__button-text', btnLoading && 'generic__button-text--loading')}
          >
            {trans.proceed}
          </span>
        </Button>
      </div>
    </div>
  )
})
